import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuthV2, type Staff } from "@/lib/authv2/store";
import { logout as callLogout } from "@/lib/authv2/api";
import { activePersona, hasPersonas, isSecondaryActive } from "@/lib/authv2/persona";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Avatar, AvatarFallback } from "@/ui/avatar";
import { Button } from "@/ui/button";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { MyAffiliateCodeDialog } from "@/components/MyAffiliateCodeDialog";
import { StaffAvatar } from "@/components/StaffAvatar";
import { PersonaMenuSection } from "@/features/persona/PersonaMenuSection";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/ui/sheet";

/**
 * Menu tài khoản (ảnh + tên → Mã AFF · Đổi mật khẩu · Đổi người · Đăng xuất).
 *
 * Tách khỏi Header từ đợt dải biểu tượng của màn Chat khách (QĐ 0223; dải đã
 * bỏ 15/10/2026, màn đó nay có thanh đầu như mọi màn). `compact` / `side` /
 * `align` giữ cho chỗ đặt menu khác thanh đầu sau này.
 *
 * Người có danh phận phụ (QĐ 0255) thấy thêm khối "Dùng với tư cách" ở đầu
 * menu; trên điện thoại menu mở thành hộp kéo từ đáy (bản mẫu v2).
 *
 * `compact`: chỉ ảnh, không tên — dải biểu tượng rộng 64px.
 */
export function UserMenu({
  compact = false,
  side = "bottom",
  align = "end",
}: {
  compact?: boolean;
  side?: "bottom" | "right";
  align?: "start" | "end";
}) {
  const displayName = useAuthV2((s) => s.displayName);
  const currentStaff = useAuthV2((s) => s.currentStaff);
  const persona = useAuthV2((s) => s.persona);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [affiliateCodeOpen, setAffiliateCodeOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const multi = hasPersonas(persona);
  const secondary = isSecondaryActive(persona);
  const activeLabel = multi ? activePersona(persona).label : null;

  const handleSwitch = () => {
    void navigate({ to: "/login" });
  };
  const handleLogout = async () => {
    try {
      await callLogout();
    } catch {
      // Best-effort — even if BE call fails (network), clear local state.
    }
    useAuthV2.getState().clear();
    void navigate({ to: "/login" });
  };

  const items = [
    { key: "aff", label: "Mã AFF của tôi", run: () => setAffiliateCodeOpen(true) },
    { key: "pw", label: "Đổi mật khẩu", run: () => setPasswordDialogOpen(true) },
    { key: "switch", label: "Đổi người", run: handleSwitch },
    { key: "logout", label: "Đăng xuất", run: () => void handleLogout() },
  ];

  const trigger = accountTrigger({ compact, displayName, currentStaff, secondary, activeLabel });

  return (
    <>
      {isMobile && multi ? (
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>{trigger}</SheetTrigger>
          <SheetContent side="bottom" className="space-y-3">
            <SheetTitle className="sr-only">Tài khoản</SheetTitle>
            <PersonaMenuSection variant="sheet" />
            <div className="border-t pt-2" style={{ borderColor: "var(--color-border)" }}>
              {items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  className="flex w-full items-center rounded-md px-2 py-2 text-left text-sm hover:bg-muted/50"
                  onClick={() => {
                    setSheetOpen(false);
                    it.run();
                  }}
                >
                  {it.label}
                </button>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
          {/* Chỉ người có danh phận phụ mới cần menu rộng — còn lại giữ y như cũ. */}
          <DropdownMenuContent
            side={side}
            align={align}
            {...(multi ? { className: "min-w-[240px]" } : {})}
          >
            <PersonaMenuSection variant="dropdown" />
            {items.map((it) => (
              <DropdownMenuItem key={it.key} onClick={it.run}>
                {it.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <MyAffiliateCodeDialog open={affiliateCodeOpen} onOpenChange={setAffiliateCodeOpen} />
      <ChangePasswordDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen} />
    </>
  );
}

/** Nút mở menu: ảnh (+ chấm màu khi đang ở danh phận phụ) và tên. */
function accountTrigger(o: {
  compact: boolean;
  displayName: string | null;
  currentStaff: Staff | null;
  secondary: boolean;
  activeLabel: string | null;
}) {
  const { compact, displayName, currentStaff, secondary, activeLabel } = o;
  return (
    <Button
      variant="ghost"
      data-testid="user-menu-trigger"
      className={compact ? "h-11 w-11 p-0" : "gap-2"}
      aria-label={compact ? `Tài khoản: ${displayName ?? "Chưa đăng nhập"}` : undefined}
    >
      <span className="relative inline-flex">
        {currentStaff ? (
          <StaffAvatar staffId={currentStaff.id} name={currentStaff.name} sizePx={32} />
        ) : (
          // Chưa link được hồ sơ nhân sự thì không có ảnh để tra — giữ
          // nguyên ô chữ cái như cũ.
          <Avatar>
            <AvatarFallback>{displayName?.[0] ?? "?"}</AvatarFallback>
          </Avatar>
        )}
        {secondary && (
          <span
            data-testid="persona-dot"
            aria-hidden
            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-surface"
            style={{ background: "var(--color-info-text)" }}
          />
        )}
      </span>
      {!compact &&
        (activeLabel ? (
          <span className="hidden flex-col items-start leading-tight sm:flex">
            <span>{displayName ?? "Chưa đăng nhập"}</span>
            <span className="text-[11px] text-muted-foreground">{activeLabel}</span>
          </span>
        ) : (
          <span className="hidden sm:inline">{displayName ?? "Chưa đăng nhập"}</span>
        ))}
    </Button>
  );
}
